import { View, Text, StyleSheet, Image, TouchableOpacity } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { Award } from 'lucide-react-native';
import { FLAGS } from '@/lib/featureFlags';

interface UserIdentityProps {
  avatarUrl?: string | null;
  displayName?: string | null;
  username?: string;
  currentRank?: string | null;
  featuredBadge?: {
    icon_name: string;
    tier: string;
    name: string;
  } | null;
  size?: 'small' | 'medium' | 'large';
  showBadge?: boolean;
  showUsername?: boolean;
}

const RANK_COLORS: Record<string, readonly [string, string, ...string[]]> = {
  bronze: ['#CD7F32', '#8B4513'],
  silver: ['#C0C0C0', '#808080'],
  gold: ['#FFD700', '#FFA500'],
  platinum: ['#E5E4E2', '#A0A0A0'],
  diamond: ['#B9F2FF', '#00BFFF'],
  master: ['#FF1744', '#C51162'],
  grandmaster: ['#7C4DFF', '#4A148C'],
};

const SIZE_CONFIG = {
  small: {
    avatar: 40,
    ring: 48,
    badge: 16,
    ringWidth: 3,
    fontSize: 16,
  },
  medium: {
    avatar: 56,
    ring: 66,
    badge: 20,
    ringWidth: 4,
    fontSize: 20,
  },
  large: {
    avatar: 80,
    ring: 92,
    badge: 28,
    ringWidth: 5,
    fontSize: 28,
  },
};

export default function UserIdentity({
  avatarUrl,
  displayName,
  username,
  currentRank = 'bronze',
  featuredBadge,
  size = 'medium',
  showBadge = true,
  showUsername = true,
}: UserIdentityProps) {
  const { theme } = useTheme();
  const router = useRouter();
  const config = SIZE_CONFIG[size];
  const rankColors = RANK_COLORS[currentRank?.toLowerCase() || 'bronze'] || RANK_COLORS.bronze;

  const safeName = displayName?.trim() || username?.trim() || 'User';
  const initials = safeName
    .split(' ')
    .map((n) => n[0] || '')
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <View style={styles.container}>
      <View style={styles.avatarContainer}>
        <LinearGradient
          colors={rankColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[
            styles.statusRing,
            {
              width: config.ring,
              height: config.ring,
              borderRadius: config.ring / 2,
              padding: config.ringWidth,
            },
          ]}>
          <View
            style={[
              styles.avatarInner,
              {
                width: config.avatar,
                height: config.avatar,
                borderRadius: config.avatar / 2,
                backgroundColor: theme.colors.surfaceVariant,
              },
            ]}>
            {avatarUrl ? (
              <Image source={{ uri: avatarUrl }} style={styles.avatarImage} />
            ) : (
              <Text
                style={[
                  styles.avatarText,
                  { color: theme.colors.text, fontSize: config.fontSize },
                ]}>
                {initials}
              </Text>
            )}
          </View>
        </LinearGradient>

        {showBadge && featuredBadge && (
          <View
            style={[
              styles.badgeContainer,
              {
                width: config.badge + 8,
                height: config.badge + 8,
                borderRadius: (config.badge + 8) / 2,
                backgroundColor: theme.colors.background,
              },
            ]}>
            <View
              style={[
                styles.badge,
                {
                  width: config.badge,
                  height: config.badge,
                  borderRadius: config.badge / 2,
                  backgroundColor: getBadgeColor(featuredBadge.tier),
                },
              ]}>
              <Award size={config.badge - 4} color={theme.colors.text} strokeWidth={2.5} />
            </View>
          </View>
        )}
      </View>

      {showUsername && (
        <View style={styles.nameContainer}>
          <View style={styles.nameRow}>
            <Text
              style={[styles.displayName, { color: theme.colors.text }]}
              numberOfLines={1}>
              {safeName}
            </Text>
            {featuredBadge && (
              <View
                style={[
                  styles.inlineBadge,
                  { backgroundColor: getBadgeColor(featuredBadge.tier) + '20' },
                ]}>
                <Award
                  size={14}
                  color={getBadgeColor(featuredBadge.tier)}
                  strokeWidth={2.5}
                />
              </View>
            )}
          </View>
          {username && (
            <Text
              style={[styles.username, { color: theme.colors.textSecondary }]}
              numberOfLines={1}>
              @{username}
            </Text>
          )}
          {FLAGS.V1_CHALLENGES_ENABLED && (
          <TouchableOpacity
            onPress={() => router.push('/challenges')}
            style={styles.challengesButton}>
            <Text style={[styles.challengesText, { color: theme.colors.accent }]}>Challenges</Text>
          </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
}

function getBadgeColor(tier: string): string {
  const colors: Record<string, string> = {
    bronze: '#CD7F32',
    silver: '#C0C0C0',
    gold: '#FFD700',
    platinum: '#E5E4E2',
    diamond: '#00BFFF',
  };
  return colors[tier.toLowerCase()] || colors.bronze;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatarContainer: {
    position: 'relative',
  },
  statusRing: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarInner: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarText: {
    fontWeight: '900',
  },
  badgeContainer: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badge: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  nameContainer: {
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  displayName: {
    fontSize: 16,
    fontWeight: '700',
  },
  username: {
    fontSize: 13,
    marginTop: 2,
  },
  inlineBadge: {
    width: 20,
    height: 20,
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  challengesButton: {
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 8,
    alignSelf: 'flex-start',
    borderRadius: 2,
  },
  challengesText: {
    fontSize: 13,
    fontWeight: '700',
  },
});
